/**
 * Hand-maintained registry mapping every bundle name to the cross-bundle
 * surface it owns: GitHub `type:*` labels, phase-label prefixes,
 * scheduled-task IDs, whether it emits Starlight docs, and whether it
 * declares any downstream issue kinds (i.e. files `gh issue create`
 * recipes via the issue-templates convention).
 *
 * The registry is consulted by renderers in other bundles whenever
 * `AgentConfigOptions.excludeBundles` is non-empty so cross-bundle
 * references to an excluded bundle's agents, type labels, phase labels,
 * or scheduled tasks disappear from the generated output.
 *
 * The map is **hand-maintained** rather than derived from each bundle's
 * runtime shape. The defining surfaces (the funnel-tier table in
 * `tiers.ts`, the per-phase scope-gate overrides in `scope-gate.ts`, and
 * the scheduled-tasks registry in `scheduled-tasks.ts`) live as flat
 * data tables that already get walked by their renderers — declaring the
 * ownership map alongside them keeps the relationship explicit and
 * readable without forcing every bundle to grow an "ownership"
 * descriptor.
 *
 * Bundles that ship no cross-bundle surface (e.g. `slack`, `typescript`,
 * `pnpm`, `vitest`, `jest`, `aws-cdk`, `projen`, `turborepo`,
 * `upstream-configulator-docs`) deliberately do not appear here —
 * excluding them is already a no-op since they own nothing other
 * bundles reference.
 */
export interface BundleOwnership {
  /**
   * GitHub `type:*` label values (without the `type:` prefix) the
   * bundle owns. The funnel-tier table in `tiers.ts` and any rendered
   * tables that group agents by `type:*` label consult this list.
   */
  readonly typeLabels: ReadonlyArray<string>;
  /**
   * Phase-label prefixes (with trailing colon, e.g. `"company:"`) the
   * bundle owns. Used by the scope-gate per-phase override table and
   * any other renderer that groups by phase label. An entry without a
   * trailing colon (e.g. `"req:write"`) is treated as an exact
   * phase-label match instead of a prefix.
   */
  readonly phaseLabelPrefixes: ReadonlyArray<string>;
  /**
   * `taskId` values from `DEFAULT_SCHEDULED_TASK_ENTRIES` that target
   * this bundle's sub-agent. The scheduled-tasks registry filter in
   * `agent-config.ts` consults this list when pruning default entries
   * for an excluded bundle.
   */
  readonly scheduledTaskIds: ReadonlyArray<string>;
  /**
   * Whether this bundle emits Starlight content roots — i.e. whether
   * any of its workflows write files under `docs/src/content/docs/`
   * (or the configured docs root). Drives the auto-suppression of the
   * `section-index-pages` rule when no docs-emitting bundle is active.
   */
  readonly emitsDocs: boolean;
  /**
   * Whether this bundle dispatches downstream issues (i.e. its
   * workflows file `gh issue create` recipes). Drives the
   * auto-suppression of the `issue-templates-convention` rule when no
   * such bundle is active.
   *
   * Not an exhaustive enumerator of issue-filing bundles. Only bundles
   * that own a cross-bundle surface appear in {@link BUNDLE_OWNERSHIP}
   * at all, so a bundle can file issues and still be absent — the
   * `upstream-configulator-docs` bundle files into a *foreign* repo
   * (`codedrifters/packages`) and is deliberately not registered.
   * Treat a `true` here as "this bundle's phase labels need the
   * templates convention", not as "these are all the filing sites".
   */
  readonly downstreamIssueKinds: boolean;
}

/**
 * Canonical ownership map. Only bundles that own at least one
 * cross-bundle surface appear here.
 */
export const BUNDLE_OWNERSHIP: Readonly<Record<string, BundleOwnership>> = {
  agenda: {
    typeLabels: ["agenda"],
    phaseLabelPrefixes: ["agenda:"],
    scheduledTaskIds: ["worker-agenda"],
    emitsDocs: true,
    downstreamIssueKinds: true,
  },
  "bcm-writer": {
    typeLabels: ["bcm-document"],
    phaseLabelPrefixes: ["bcm:"],
    scheduledTaskIds: ["worker-bcm-writer"],
    emitsDocs: true,
    downstreamIssueKinds: true,
  },
  "business-models": {
    typeLabels: ["business-model"],
    phaseLabelPrefixes: ["business-models:"],
    scheduledTaskIds: ["worker-business-models"],
    emitsDocs: true,
    downstreamIssueKinds: true,
  },
  "company-profile": {
    typeLabels: ["company-profile"],
    phaseLabelPrefixes: ["company:"],
    scheduledTaskIds: ["worker-company-profile"],
    emitsDocs: true,
    downstreamIssueKinds: true,
  },
  "customer-profile": {
    typeLabels: ["customer-profile"],
    phaseLabelPrefixes: ["customer:"],
    scheduledTaskIds: ["worker-customer-profile"],
    emitsDocs: true,
    downstreamIssueKinds: true,
  },
  "docs-sync": {
    typeLabels: ["docs-sync"],
    phaseLabelPrefixes: ["docs-sync:"],
    scheduledTaskIds: ["worker-docs-sync"],
    emitsDocs: true,
    downstreamIssueKinds: true,
  },
  "industry-discovery": {
    typeLabels: ["industry-discovery"],
    phaseLabelPrefixes: ["industry:"],
    scheduledTaskIds: ["worker-industry-discovery"],
    emitsDocs: true,
    downstreamIssueKinds: true,
  },
  "maintenance-audit": {
    typeLabels: ["maintenance"],
    phaseLabelPrefixes: ["maint:"],
    scheduledTaskIds: ["worker-maintenance"],
    emitsDocs: false,
    downstreamIssueKinds: true,
  },
  "meeting-analysis": {
    typeLabels: ["meeting-processing"],
    phaseLabelPrefixes: ["meeting:"],
    scheduledTaskIds: ["worker-meeting-analysis"],
    emitsDocs: true,
    downstreamIssueKinds: true,
  },
  orchestrator: {
    // The orchestrator pipeline manager itself ships with the
    // `worker-orchestrator` scheduled task. It owns no `type:*` label
    // (it dispatches every type) and no phase-label prefix.
    typeLabels: [],
    phaseLabelPrefixes: [],
    scheduledTaskIds: ["worker-orchestrator"],
    emitsDocs: false,
    downstreamIssueKinds: false,
  },
  "people-profile": {
    typeLabels: ["people-profile"],
    phaseLabelPrefixes: ["people:"],
    scheduledTaskIds: ["worker-people-profile"],
    emitsDocs: true,
    downstreamIssueKinds: true,
  },
  "pr-review": {
    typeLabels: ["pr-review"],
    phaseLabelPrefixes: [],
    scheduledTaskIds: ["worker-pr-review"],
    emitsDocs: false,
    downstreamIssueKinds: false,
  },
  "regulatory-research": {
    typeLabels: ["regulatory-research"],
    phaseLabelPrefixes: ["regulatory:"],
    scheduledTaskIds: ["worker-regulatory-research"],
    emitsDocs: true,
    downstreamIssueKinds: true,
  },
  "requirements-analyst": {
    typeLabels: ["requirement"],
    phaseLabelPrefixes: ["req:"],
    scheduledTaskIds: ["worker-requirements-analyst"],
    emitsDocs: true,
    downstreamIssueKinds: true,
  },
  "requirements-reviewer": {
    // Co-owns `type:requirement` with the analyst and writer; owns
    // the `req:review` and `req:deprecate` phase labels exactly.
    typeLabels: ["requirement"],
    phaseLabelPrefixes: ["req:review", "req:deprecate"],
    scheduledTaskIds: ["worker-requirements-reviewer"],
    emitsDocs: true,
    downstreamIssueKinds: true,
  },
  "requirements-writer": {
    // Co-owns `type:requirement` with the analyst and reviewer; owns
    // the `req:write` phase label exactly.
    typeLabels: ["requirement"],
    phaseLabelPrefixes: ["req:write"],
    scheduledTaskIds: ["worker-requirements-writer"],
    emitsDocs: true,
    downstreamIssueKinds: true,
  },
  "research-pipeline": {
    typeLabels: ["research"],
    phaseLabelPrefixes: ["research:"],
    scheduledTaskIds: ["worker-research"],
    emitsDocs: true,
    downstreamIssueKinds: true,
  },
  "software-profile": {
    typeLabels: ["software-profile"],
    phaseLabelPrefixes: ["software:"],
    scheduledTaskIds: ["worker-software-profile"],
    emitsDocs: true,
    downstreamIssueKinds: true,
  },
  "standards-research": {
    typeLabels: ["standards-research"],
    phaseLabelPrefixes: ["standards:"],
    scheduledTaskIds: ["worker-standards-research"],
    emitsDocs: true,
    downstreamIssueKinds: true,
  },
};

/**
 * GitHub `type:*` labels (WITH the `type:` prefix) that come from the
 * **conventional-commit** vocabulary rather than the bundle/routing
 * vocabulary. These are derived from an issue's title prefix by the
 * generic create-issue workflow (`feat:` → `type:feat`, `docs:` →
 * `type:docs`, …) and are the only `type:*` labels the phase-label
 * invariant is allowed to remove when it corrects a mislabeled issue.
 *
 * A bundle `type:*` label (e.g. `type:research`, `type:bcm-document`)
 * is deliberately **not** in this set: an issue carrying a phase label
 * from one bundle plus a `type:*` label owned by a *different* bundle
 * is genuinely ambiguous and gets flagged for a human rather than
 * silently rewritten.
 */
export const CONVENTIONAL_COMMIT_TYPE_LABELS: ReadonlyArray<string> = [
  "type:chore",
  "type:docs",
  "type:feat",
  "type:fix",
  "type:hotfix",
  "type:refactor",
  "type:release",
];

/**
 * Canonical phase-label matcher → `type:<bundle>` label map, derived
 * from {@link BUNDLE_OWNERSHIP}. This is the **single source of truth**
 * for the phase-label → type-label invariant: label registry
 * generation, the orchestrator's triage sweep, and the consumer-facing
 * label audit all read this map rather than re-deriving the pairing.
 *
 * Keys are matchers in the same notation `BundleOwnership.phaseLabelPrefixes`
 * uses — an entry ending in a colon (`"company:"`) is a prefix match,
 * an entry without one (`"req:write"`) is an exact match. Values carry
 * the `type:` prefix.
 *
 * Co-ownership is fine as long as the co-owners agree on the type
 * label: all three requirements bundles declare `type:requirement`, so
 * `req:`, `req:write`, `req:review`, and `req:deprecate` all resolve to
 * the same value. A matcher that resolved to two *different* type
 * labels would be a registry bug and throws at module load.
 */
export const PHASE_LABEL_TYPE_MAP: Readonly<Record<string, string>> =
  buildPhaseLabelTypeMap();

/**
 * Outcome of resolving a set of issue labels against
 * {@link PHASE_LABEL_TYPE_MAP}.
 *
 * - `"none"` — the labels carry no **recognised** phase label, so the
 *   invariant does not apply. Unrecognised `foo:bar` labels are
 *   consumer-specific and deliberately not policed.
 * - `"match"` — the recognised phase labels all imply one and the same
 *   `type:<bundle>` label, carried in `typeLabel`.
 * - `"ambiguous"` — the recognised phase labels imply two or more
 *   different `type:<bundle>` labels. Never auto-corrected; the caller
 *   flags the issue for human triage instead.
 */
export type PhaseLabelTypeOutcome = "none" | "match" | "ambiguous";

/** Result of {@link resolveTypeLabelForLabels}. */
export interface PhaseLabelTypeResolution {
  /** Which of the three outcomes applies. */
  readonly outcome: PhaseLabelTypeOutcome;
  /**
   * The single implied `type:<bundle>` label (with the `type:` prefix)
   * when `outcome` is `"match"`; `undefined` otherwise.
   */
  readonly typeLabel?: string;
  /**
   * Every distinct implied `type:<bundle>` label, sorted. Empty on
   * `"none"`, one entry on `"match"`, two or more on `"ambiguous"`.
   */
  readonly candidateTypeLabels: ReadonlyArray<string>;
  /**
   * The subset of the input labels that matched a phase-label matcher,
   * in input order. Empty on `"none"`.
   */
  readonly phaseLabels: ReadonlyArray<string>;
}

/**
 * Resolve a single phase label to the `type:<bundle>` label its owning
 * bundle declares, or `undefined` when no bundle owns it.
 *
 * Exact-match entries beat prefix entries: `req:write` is owned by
 * `requirements-writer` while the `req:` prefix is owned by
 * `requirements-analyst`. (Both currently declare `type:requirement`,
 * but the precedence is load-bearing for any future divergence.)
 */
export function typeLabelForPhaseLabel(phaseLabel: string): string | undefined {
  const exact = PHASE_LABEL_TYPE_MAP[phaseLabel];
  if (exact !== undefined && !phaseLabel.endsWith(":")) {
    return exact;
  }
  let bestMatcher: string | undefined;
  for (const matcher of Object.keys(PHASE_LABEL_TYPE_MAP)) {
    if (!matcher.endsWith(":")) {
      continue;
    }
    if (!phaseLabel.startsWith(matcher)) {
      continue;
    }
    // Longest prefix wins so a future `req:sub:` never loses to `req:`.
    if (bestMatcher === undefined || matcher.length > bestMatcher.length) {
      bestMatcher = matcher;
    }
  }
  return bestMatcher === undefined
    ? undefined
    : PHASE_LABEL_TYPE_MAP[bestMatcher];
}

/**
 * Resolve every label on an issue to the `type:<bundle>` label the
 * phase-label invariant requires it to carry.
 *
 * The input is the issue's **full** label list — the resolver picks out
 * the recognised phase labels itself and ignores everything else
 * (`status:*`, `priority:*`, existing `type:*`, and any consumer label
 * that matches no bundle).
 */
export function resolveTypeLabelForLabels(
  labels: ReadonlyArray<string>,
): PhaseLabelTypeResolution {
  const phaseLabels: Array<string> = [];
  const candidates = new Set<string>();
  for (const label of labels) {
    const typeLabel = typeLabelForPhaseLabel(label);
    if (typeLabel === undefined) {
      continue;
    }
    phaseLabels.push(label);
    candidates.add(typeLabel);
  }
  const candidateTypeLabels = Array.from(candidates).sort();
  if (candidateTypeLabels.length === 0) {
    return { outcome: "none", candidateTypeLabels: [], phaseLabels: [] };
  }
  if (candidateTypeLabels.length === 1) {
    return {
      outcome: "match",
      typeLabel: candidateTypeLabels[0],
      candidateTypeLabels,
      phaseLabels,
    };
  }
  return { outcome: "ambiguous", candidateTypeLabels, phaseLabels };
}

/**
 * Render the **Phase-label → `type:<bundle>` invariant** section of the
 * `orchestrator-conventions` rule. The matcher table is generated from
 * {@link PHASE_LABEL_TYPE_MAP}, so the documented pairing can never
 * drift from the pairing the sweep enforces.
 *
 * Rows whose owning bundle appears in `excludeBundles` are dropped,
 * matching every other cross-bundle renderer.
 */
export function renderPhaseTypeInvariantSection(
  excludeBundles: ReadonlyArray<string> = [],
): string {
  const rows = Object.keys(PHASE_LABEL_TYPE_MAP)
    .filter(
      (matcher) => !isPhaseLabelMatcherOwnedByExcluded(matcher, excludeBundles),
    )
    .sort()
    .map((matcher) => {
      const display = matcher.endsWith(":") ? `${matcher}*` : matcher;
      const kind = matcher.endsWith(":") ? "prefix" : "exact";
      return `| \`${display}\` | ${kind} | \`${PHASE_LABEL_TYPE_MAP[matcher]}\` |`;
    });

  return [
    "## Phase-label → `type:<bundle>` invariant",
    "",
    "Every phased-pipeline bundle pairs its `<bundle>:<phase>` labels",
    "with exactly one `type:<bundle>` label. That type label is the",
    "**dedup + dispatch signal**: agents de-duplicate downstream work",
    'with `gh issue list --label "type:<bundle>"`, and Phase E derives',
    "its funnel-tier sort key from the issue's `type:*` label. An issue",
    "that carries the phase label but a conventional-commit `type:*`",
    "(`type:feat`, `type:docs`, …, stamped from its title prefix by the",
    "generic create-issue workflow) is invisible to that dedup query and",
    "mis-tiers in dispatch.",
    "",
    "The pairing below is derived from the canonical bundle-ownership",
    "map — the same source of truth that generates the label registry.",
    "Matchers ending in `*` match by prefix; the rest match exactly, and",
    "an exact match always beats a prefix match.",
    "",
    "| Phase label | Match | Required type label |",
    "|-------------|-------|---------------------|",
    ...rows,
    "",
    "### Enforcement",
    "",
    "The Phase D maintenance sweep runs the invariant in auto-correct",
    "mode and emits one summary line:",
    "",
    "```",
    "LABEL_INVARIANT_DONE mode=fix checked=<N> ok=<O> violations=<V> corrected=<C> flagged=<F>",
    "```",
    "",
    "A **report-only** audit is available for repos that prefer",
    "report-then-fix over auto-correct — same sweep, no mutations:",
    "",
    "```bash",
    ".claude/procedures/check-blocked.sh label-invariant        # report only",
    ".claude/procedures/check-blocked.sh label-invariant --fix  # apply",
    "```",
    "",
    "### Correction policy",
    "",
    "An issue must carry **exactly one** `type:*` label. Merely *adding*",
    "the required type label to an issue that already carries a",
    "conventional-commit one would leave two, making the funnel-tier sort",
    "key (which reads the **first** `type:*` label) non-deterministic and",
    "violating the label conventions. The correction therefore",
    "**replaces**, in a single atomic `gh issue edit`, so the issue is",
    "never observable carrying two `type:*` labels:",
    "",
    "| Issue state | Action |",
    "|-------------|--------|",
    "| Required type label present, nothing else | none — compliant, left untouched |",
    "| No `type:*` label at all | **add** the required label |",
    "| Only conventional-commit `type:*` label(s) | **replace** — remove them, add the required label |",
    "| Required label present **plus** a stray conventional-commit one | **remove** the stray, leaving exactly one |",
    "| A `type:*` label owned by a **different** bundle | **flag** `status:needs-attention` — never guessed at |",
    "| Phase labels imply **two or more** type labels | **flag** `status:needs-attention` — never auto-corrected |",
    "",
    "Only a conventional-commit `type:*` label is ever removed. A",
    "`type:*` label owned by another bundle carries real routing",
    "information, so the sweep refuses to guess and hands the issue to a",
    "human instead. Ambiguity is narrower than it looks: all three",
    "requirements bundles declare `type:requirement`, so every `req:*`",
    "phase label resolves to the same type label — genuine ambiguity",
    "needs phase labels from two bundles with *different* type labels.",
    "",
    "`status:needs-attention` is applied **additively** — the base",
    "`status:*` label always stays, per the additive-flag rule. Flagging",
    "is idempotent: an issue already carrying the flag is reported but",
    "not re-flagged.",
  ].join("\n");
}

/**
 * Return `true` when the bundle(s) declaring `matcher` are all in
 * `excludeBundles`.
 */
function isPhaseLabelMatcherOwnedByExcluded(
  matcher: string,
  excludeBundles: ReadonlyArray<string>,
): boolean {
  if (excludeBundles.length === 0) {
    return false;
  }
  let owned = false;
  for (const [bundleName, ownership] of Object.entries(BUNDLE_OWNERSHIP)) {
    if (!ownership.phaseLabelPrefixes.includes(matcher)) {
      continue;
    }
    owned = true;
    if (!excludeBundles.includes(bundleName)) {
      return false;
    }
  }
  return owned;
}

/**
 * Render the POSIX-shell half of the phase-label → `type:<bundle>`
 * invariant, derived from the same {@link PHASE_LABEL_TYPE_MAP} the
 * TypeScript accessors read. Emitted into `check-blocked.sh` so the
 * orchestrator's triage sweep and the consumer-runnable label audit
 * never carry a hand-copied second map.
 *
 * Three functions are rendered:
 *
 * - `phase_label_type_of <label>` — echoes the `type:<bundle>` label a
 *   single phase label implies, or nothing. Exact-match branches are
 *   emitted before prefix branches so `case` ordering reproduces the
 *   exact-beats-prefix precedence.
 * - `phase_type_of` — reads an issue's labels (one per line) on stdin
 *   and emits `KEY=VALUE` assignments: `OUTCOME=none|match|ambiguous`,
 *   `TYPE_LABEL=` (match only), `CANDIDATE_TYPE_LABELS=` (ambiguous
 *   only), and `PHASE_LABELS=`.
 * - `is_conventional_type_label <label>` — returns 0 for a
 *   conventional-commit `type:*` label, i.e. the only labels the
 *   auto-correction is allowed to remove.
 */
export function renderPhaseTypeInvariantShellHelpers(): string {
  const matchers = Object.keys(PHASE_LABEL_TYPE_MAP);
  const exactMatchers = matchers.filter((m) => !m.endsWith(":")).sort();
  // Longest prefix first so a future `req:sub:` is tested before `req:`.
  const prefixMatchers = matchers
    .filter((m) => m.endsWith(":"))
    .sort((a, b) => b.length - a.length || a.localeCompare(b));

  const lines: Array<string> = [
    "# Resolve ONE phase label to the `type:<bundle>` label its owning",
    "# bundle declares. Echoes the label (with the `type:` prefix) or",
    "# nothing when no bundle owns it. Exact-match branches come first,",
    "# so `req:write` (requirements-writer) beats the `req:` prefix",
    "# (requirements-analyst). Generated from BUNDLE_OWNERSHIP — do not",
    "# hand-edit.",
    "phase_label_type_of() {",
    '  case "${1:-}" in',
  ];
  for (const matcher of exactMatchers) {
    lines.push(`    ${matcher}) echo "${PHASE_LABEL_TYPE_MAP[matcher]}" ;;`);
  }
  for (const matcher of prefixMatchers) {
    lines.push(`    ${matcher}*) echo "${PHASE_LABEL_TYPE_MAP[matcher]}" ;;`);
  }
  lines.push(
    "    *) : ;;",
    "  esac",
    "}",
    "",
    "# Return 0 when the argument is a conventional-commit `type:*`",
    "# label — the ONLY type labels the invariant correction may remove.",
    "# A `type:*` label owned by another bundle is never removed; the",
    "# issue is flagged for human triage instead.",
    "is_conventional_type_label() {",
    `  case "\${1:-}" in`,
    `    ${CONVENTIONAL_COMMIT_TYPE_LABELS.join("|")}) return 0 ;;`,
    "    *) return 1 ;;",
    "  esac",
    "}",
    "",
    "# Resolve an issue's FULL label list (one label per line on stdin)",
    "# to the type label the phase-label invariant requires. Emits",
    "# KEY=VALUE assignments the caller parses:",
    "#   OUTCOME=none      — no recognised phase label; invariant N/A",
    "#   OUTCOME=match     — plus TYPE_LABEL=<type:bundle>",
    "#   OUTCOME=ambiguous — plus CANDIDATE_TYPE_LABELS=<space-separated>",
    "#   PHASE_LABELS=<space-separated recognised phase labels>",
    "phase_type_of() {",
    "  local label resolved",
    '  local matched_types=""',
    '  local matched_phase=""',
    "  while IFS= read -r label; do",
    '    [ -z "$label" ] && continue',
    '    resolved=$(phase_label_type_of "$label")',
    '    [ -z "$resolved" ] && continue',
    '    matched_phase="${matched_phase}${label} "',
    '    case " ${matched_types} " in',
    '      *" ${resolved} "*) ;;',
    '      *) matched_types="${matched_types}${resolved} " ;;',
    "    esac",
    "  done",
    "  local count=0",
    "  for resolved in $matched_types; do",
    "    count=$((count + 1))",
    "  done",
    '  if [ "$count" -eq 0 ]; then',
    '    echo "OUTCOME=none"',
    '  elif [ "$count" -eq 1 ]; then',
    '    echo "OUTCOME=match"',
    '    echo "TYPE_LABEL=${matched_types% }"',
    "  else",
    '    echo "OUTCOME=ambiguous"',
    '    echo "CANDIDATE_TYPE_LABELS=${matched_types% }"',
    "  fi",
    '  echo "PHASE_LABELS=${matched_phase% }"',
    "}",
  );
  return lines.join("\n");
}

/**
 * Build {@link PHASE_LABEL_TYPE_MAP} from {@link BUNDLE_OWNERSHIP}.
 *
 * @throws `Error` when two bundles claim the same phase-label matcher
 *         with different `type:*` labels, or when a bundle declares a
 *         phase-label matcher without declaring exactly one type label
 *         to pair it with — both are unresolvable registry bugs.
 */
function buildPhaseLabelTypeMap(): Readonly<Record<string, string>> {
  const map: Record<string, string> = {};
  for (const [bundleName, ownership] of Object.entries(BUNDLE_OWNERSHIP)) {
    if (ownership.phaseLabelPrefixes.length === 0) {
      continue;
    }
    if (ownership.typeLabels.length !== 1) {
      throw new Error(
        `BUNDLE_OWNERSHIP["${bundleName}"] declares ${ownership.phaseLabelPrefixes.length} phase-label matcher(s) but ${ownership.typeLabels.length} type label(s); a phase label must pair with exactly one type label`,
      );
    }
    const typeLabel = `type:${ownership.typeLabels[0]}`;
    for (const matcher of ownership.phaseLabelPrefixes) {
      const existing = map[matcher];
      if (existing !== undefined && existing !== typeLabel) {
        throw new Error(
          `Phase-label matcher "${matcher}" resolves to both "${existing}" and "${typeLabel}"; a matcher must imply exactly one type label`,
        );
      }
      map[matcher] = typeLabel;
    }
  }
  return map;
}

/**
 * Return `true` when `typeLabel` (without the leading `type:` prefix)
 * is owned by any bundle in `excludedBundles`. Used by tier-table and
 * scheduled-task renderers to drop rows whose owning bundle has been
 * excluded.
 */
export function isTypeLabelOwnedByExcluded(
  typeLabel: string,
  excludedBundles: ReadonlyArray<string>,
): boolean {
  if (excludedBundles.length === 0) {
    return false;
  }
  for (const bundleName of excludedBundles) {
    const ownership = BUNDLE_OWNERSHIP[bundleName];
    if (!ownership) {
      continue;
    }
    if (ownership.typeLabels.includes(typeLabel)) {
      // Co-owned `type:requirement` is the only multi-bundle case.
      // Drop the row only when **every** bundle that owns the label
      // is excluded, otherwise the still-active co-owner needs the
      // row to reach its own scheduled task.
      const owners = findOwnersOfTypeLabel(typeLabel);
      const allExcluded = owners.every((owner) =>
        excludedBundles.includes(owner),
      );
      if (allExcluded) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Return `true` when `phaseLabel` is owned by any bundle in
 * `excludedBundles`. Matches against both prefix entries (with
 * trailing colon, e.g. `"company:"`) and exact-match entries (without
 * trailing colon, e.g. `"req:write"`). Used by the scope-gate
 * per-phase-override table renderer.
 */
export function isPhaseLabelOwnedByExcluded(
  phaseLabel: string,
  excludedBundles: ReadonlyArray<string>,
): boolean {
  if (excludedBundles.length === 0) {
    return false;
  }
  for (const bundleName of excludedBundles) {
    const ownership = BUNDLE_OWNERSHIP[bundleName];
    if (!ownership) {
      continue;
    }
    for (const entry of ownership.phaseLabelPrefixes) {
      if (entry.endsWith(":")) {
        if (phaseLabel.startsWith(entry)) {
          return true;
        }
      } else if (phaseLabel === entry) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Return `true` when the scheduled-task `taskId` is owned by any
 * bundle in `excludedBundles`. Used by the scheduled-tasks registry
 * filter to drop default entries pointing at an excluded bundle.
 */
export function isScheduledTaskOwnedByExcluded(
  taskId: string,
  excludedBundles: ReadonlyArray<string>,
): boolean {
  if (excludedBundles.length === 0) {
    return false;
  }
  for (const bundleName of excludedBundles) {
    const ownership = BUNDLE_OWNERSHIP[bundleName];
    if (!ownership) {
      continue;
    }
    if (ownership.scheduledTaskIds.includes(taskId)) {
      return true;
    }
  }
  return false;
}

/**
 * Return `true` when at least one docs-emitting bundle is **not**
 * excluded. Used by the `section-index-pages` rule auto-suppression
 * gate — when this returns `false`, the rule is dropped from the
 * rendered rule map entirely.
 */
export function hasAnyDocsEmittingBundle(
  excludedBundles: ReadonlyArray<string>,
): boolean {
  for (const [bundleName, ownership] of Object.entries(BUNDLE_OWNERSHIP)) {
    if (!ownership.emitsDocs) {
      continue;
    }
    if (!excludedBundles.includes(bundleName)) {
      return true;
    }
  }
  return false;
}

/**
 * Return `true` when at least one downstream-issue-kind bundle is
 * **not** excluded. Used by the `issue-templates-convention`
 * auto-suppression gate — when this returns `false`, the rule body
 * renders the disabled-stub variant.
 */
export function hasAnyDownstreamIssueKindBundle(
  excludedBundles: ReadonlyArray<string>,
): boolean {
  for (const [bundleName, ownership] of Object.entries(BUNDLE_OWNERSHIP)) {
    if (!ownership.downstreamIssueKinds) {
      continue;
    }
    if (!excludedBundles.includes(bundleName)) {
      return true;
    }
  }
  return false;
}

/**
 * Find every bundle that declares ownership of `typeLabel`. Returns an
 * empty array when no bundle in the registry owns the label. Used by
 * `isTypeLabelOwnedByExcluded` to handle co-owned labels (e.g.
 * `type:requirement` is owned by three bundles — the row stays in the
 * table unless every co-owner is excluded).
 */
function findOwnersOfTypeLabel(typeLabel: string): ReadonlyArray<string> {
  const owners: Array<string> = [];
  for (const [bundleName, ownership] of Object.entries(BUNDLE_OWNERSHIP)) {
    if (ownership.typeLabels.includes(typeLabel)) {
      owners.push(bundleName);
    }
  }
  return owners;
}
